/**
 * Phase: "navigating" — shows real-time navigation progress (3-phase status),
 * robot state chip, multi-stop indicator avatars, and Cancel / Return to Base
 * / Change Destination.
 *
 * Also renders the "returningToBase" phase: RoomNumberPageAero passes
 * `returningToBase: true` plus a synthesized single-destination leg ("Base"
 * as the lone stop, "Almost home." as the status) instead of a separate component.
 */
import React, { useState } from "react";
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import NavigationIcon from "@mui/icons-material/Navigation";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import HomeIcon from "@mui/icons-material/Home";
import SensorsIcon from "@mui/icons-material/Sensors";
import CancelIcon from "@mui/icons-material/Cancel";
import PropTypes from "prop-types";

import DeliveryLayout from "../../../components/delivery/DeliveryLayout";
import SectionCard from "../../../components/delivery/SectionCard";
import ActionButtonRow from "../../../components/delivery/ActionButtonRow";
import DoorControl from "../../../components/delivery/DoorControl";
import ProgressBar from "../../../components/delivery/ProgressBar";
import RobotStateChip from "../../../components/delivery/RobotStateChip";
import BoxedInNotice from "../../../components/delivery/BoxedInNotice";
import StopInfoStrip from "../../../components/delivery/StopInfoStrip";
import { getStopLabel } from "../deliveryConstants";
import { actionBtnSx } from "../deliveryStyles";
import { tokens } from "../../../theme";

// Stop chip colours: completed stops read green, the current stop is filled
// accent, and upcoming stops stay muted.
const stopChipStyle = (index, currentIndex) => {
  if (index < currentIndex) {
    return { background: tokens.successSoft, color: tokens.success };
  }
  if (index === currentIndex) {
    return { background: tokens.accent, color: "#fff" };
  }
  return { background: tokens.surface3, color: tokens.text3 };
};

const NavigatingPhase = ({
  selectedMap,
  navigationTarget,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  portalRobots,
  onRobotChange,
  onEmergencyResume,
  isDoorOpen,
  onDoorPress,
  isAeroRobot,
  robotState,
  isRobotConnected,
  currentStatus,
  navProgress,
  destinations = [],
  currentStopIndex = 0,
  currentDest,
  returningToBase = false,
  onStopNavigation,
  onStopInPlace,
  onReturnToBase,
  onChangeDestination,
  boxedIn = null,
}) => {
  const fromLabel = currentStopIndex > 0 ? destinations[currentStopIndex - 1]?.name : "Base";
  const [emergencyOpen, setEmergencyOpen] = useState(false);

  const handleStopHere = () => {
    setEmergencyOpen(false);
    onStopInPlace?.();
  };

  const handleConfirmReturnToBase = () => {
    setEmergencyOpen(false);
    onStopNavigation();
  };

  let subtitle = "";
  if (currentDest) {
    subtitle = currentDest.name;
    if (destinations.length > 1) {
      subtitle += ` — Stop ${getStopLabel(currentStopIndex)} of ${destinations.length}`;
    }
  }

  const cardTitle = returningToBase ? "Returning to Base" : "Reaching Destination";

  const rightPanel = (
    <SectionCard
      icon={
        <Avatar
          sx={{ width: 36, height: 36, borderRadius: "10px", background: "rgba(255,255,255,0.15)" }}
        >
          <NavigationIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Avatar>
      }
      title={cardTitle}
      subtitle={subtitle}
      headerBg={`linear-gradient(135deg, ${tokens.accent} 0%, ${tokens.accent2} 100%)`}
      headerDirection="row"
    >
      <DoorControl isDoorOpen={isDoorOpen} onDoorPress={onDoorPress} isAeroRobot={isAeroRobot} />

      <StopInfoStrip
        from={fromLabel}
        to={currentDest?.name}
        trailingLabel={destinations.length > 1 ? "STOP" : undefined}
        trailingValue={
          destinations.length > 1
            ? `${getStopLabel(currentStopIndex)} of ${destinations.length}`
            : undefined
        }
      />

      {/* Robot motion state badge */}
      <RobotStateChip
        state={robotState}
        isRobotConnected={isRobotConnected !== false}
        hasSelectedRobot={Boolean(robot)}
      />

      {/* Boxed in: holding mid-trip, not finished — it carries on by itself */}
      <BoxedInNotice view={boxedIn} showTarget={false} />

      {/* Navigation status box — 3 phases */}
      {(() => {
        let phase = 0;
        if (navProgress >= 67) phase = 2;
        // A boxed-in robot is mid-trip, not still initializing.
        else if (robotState === "MOVING" || boxedIn) phase = 1;
        const phases = [
          {
            emoji: "🤖",
            title: "Initializing Navigation",
            subtitle: "Your service is on the move...",
          },
          { emoji: "📤", title: "Heading to Destination", subtitle: "We are on our way." },
          { emoji: "📤", title: "Almost There!", subtitle: currentStatus },
        ];
        const { emoji, title, subtitle: stepSubtitle } = phases[phase];
        return (
          <Box
            sx={{
              p: 2,
              background: tokens.surface2,
              border: `1px solid ${tokens.border}`,
              borderRadius: "12px",
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} mb={1.25}>
              <SensorsIcon sx={{ fontSize: 15, color: tokens.accent }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, flex: 1 }}>
                Navigation Status
              </Typography>
              {/* Animated bouncing dots */}
              <Stack direction="row" gap={0.5}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: tokens.accent,
                      animation: `bounce 1.2s ${i * 0.2}s infinite`,
                    }}
                  />
                ))}
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{
                p: 1.25,
                background: tokens.successSoft,
                borderRadius: "8px",
                border: `1px solid ${tokens.success}`,
              }}
            >
              <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{emoji}</Typography>
              <Box>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: tokens.success, lineHeight: 1.3 }}
                >
                  {title}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 400, color: tokens.text2, mt: 0.25 }}>
                  {stepSubtitle}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })()}

      <ProgressBar
        value={navProgress}
        color={`linear-gradient(90deg,${tokens.accent2},${tokens.accent})`}
      />

      {/* Multi-stop indicator row */}
      {destinations.length > 1 && (
        <Stack direction="row" gap={1}>
          {destinations.map((d, i) => {
            const stopStyle = stopChipStyle(i, currentStopIndex);
            return (
              <Avatar
                key={d.id}
                title={d.name}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "default",
                  background: stopStyle.background,
                  color: stopStyle.color,
                  border:
                    i === currentStopIndex ? `3px solid ${tokens.accent}` : "2px solid transparent",
                  boxShadow: i === currentStopIndex ? `0 0 0 2px ${tokens.accentSoft}` : "none",
                }}
              >
                {getStopLabel(i)}
              </Avatar>
            );
          })}
        </Stack>
      )}

      {/* Cancel Navigation + Return to Base + Change Destination.
          returningToBase suppresses Return to Base/Change Destination (there is
          no "further" destination while already homeward) exactly as the
          synthesized single-leg props leave `destinations.length <= 1`. */}
      <ActionButtonRow>
        <Button
          variant="contained"
          onClick={() => setEmergencyOpen(true)}
          startIcon={<CancelIcon sx={{ fontSize: "16px !important" }} />}
          sx={{
            ...actionBtnSx,
            flex: 1,
            background: tokens.warning,
            color: "#fff",
            "&:hover": { background: tokens.warning },
          }}
        >
          Cancel Navigation
        </Button>
        {!returningToBase && (
          <Button
            variant="outlined"
            onClick={onReturnToBase}
            startIcon={<HomeIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              ...actionBtnSx,
              flex: 1,
              color: tokens.accent,
              borderColor: tokens.accent,
              background: tokens.accentSoft,
              "&:hover": { background: tokens.accentSoft, borderColor: tokens.accent },
            }}
          >
            Return to Base
          </Button>
        )}
        {!returningToBase && (
          <Tooltip
            title={
              robotState === "MOVING"
                ? "Robot is moving — stop it before changing the destination"
                : ""
            }
          >
            <span style={{ flex: 1, display: "flex" }}>
              <Button
                variant="outlined"
                onClick={onChangeDestination}
                disabled={robotState === "MOVING"}
                startIcon={<NavigationIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  ...actionBtnSx,
                  flex: 1,
                  color: tokens.text2,
                  borderColor: tokens.border,
                  background: tokens.surface2,
                  "&:hover": { background: tokens.surface3, borderColor: tokens.borderStrong },
                }}
              >
                Change Dest
              </Button>
            </span>
          </Tooltip>
        )}
      </ActionButtonRow>
    </SectionCard>
  );

  return (
    <>
      <DeliveryLayout
        selectedMap={selectedMap}
        navigationTarget={navigationTarget}
        robot={robot}
        fromRobotId={fromRobotId}
        batteryLevel={batteryLevel}
        batteryCharging={batteryCharging}
        showMapSelectionModalDialog={showMapSelectionModalDialog}
        closeMapSelectionDialogModal={closeMapSelectionDialogModal}
        portalRobots={portalRobots}
        onRobotChange={onRobotChange}
        onEmergencyResume={onEmergencyResume}
        isRobotConnected={isRobotConnected}
        navProgress={navProgress}
        rightPanel={rightPanel}
      />

      {/* ── Cancel Navigation confirmation dialog ── */}
      <Dialog open={emergencyOpen} onClose={() => setEmergencyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: tokens.warning }}>
          <CancelIcon sx={{ color: tokens.warning }} />
          Cancel Navigation
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {returningToBase
              ? "This will cancel the return-to-base task."
              : "This will cancel the current navigation task. Choose whether the robot should stop right where it is, or head back to base."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => setEmergencyOpen(false)}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 13 }}
          >
            Keep Navigating
          </Button>
          {onStopInPlace && (
            <Button
              onClick={handleStopHere}
              variant="contained"
              startIcon={<StopCircleIcon />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                background: tokens.warning,
                color: "#fff",
                "&:hover": { background: tokens.warning },
              }}
            >
              Stop Here
            </Button>
          )}
          <Button
            onClick={handleConfirmReturnToBase}
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              background: tokens.warning,
              color: "#fff",
              "&:hover": { background: tokens.warning },
            }}
          >
            {returningToBase ? "Confirm Cancel" : "Return to Base"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

NavigatingPhase.propTypes = {
  selectedMap: PropTypes.object,
  navigationTarget: PropTypes.object,
  robot: PropTypes.object,
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isDoorOpen: PropTypes.bool,
  onDoorPress: PropTypes.func,
  isAeroRobot: PropTypes.bool,
  robotState: PropTypes.string,
  isRobotConnected: PropTypes.bool,
  currentStatus: PropTypes.string,
  navProgress: PropTypes.number,
  destinations: PropTypes.array,
  currentStopIndex: PropTypes.number,
  currentDest: PropTypes.object,
  /** True for the "returningToBase" phase — hides Return to Base/Change Dest,
   *  since there is no further destination while already heading home. */
  returningToBase: PropTypes.bool,
  onStopNavigation: PropTypes.func,
  /** Cancel-in-place — only offered while actively navigating (not returning). */
  onStopInPlace: PropTypes.func,
  onReturnToBase: PropTypes.func,
  onChangeDestination: PropTypes.func,
  /** describeBoxedIn() view from useBoxedIn, or null when not boxed in. */
  boxedIn: PropTypes.object,
};

export default NavigatingPhase;
